/**
 * Message lookup, interpolation and plurals.
 *
 * Hand-rolled rather than i18next: three languages, one bundle, and the two
 * things a library would really buy us — plural rules and locale formatting —
 * are already in the platform as `Intl.PluralRules` and `Intl.DateTimeFormat`.
 * What we get in exchange is compile-time key checking, which means a missing
 * translation is a type error rather than a string that says `nav.today`.
 */

import { en } from './messages/en';
import { getIntlLocale, getLanguage, type Language } from './store';

/**
 * A plural form set. `other` is required — it is the fallback for any category
 * a language does not distinguish.
 *
 * Czech genuinely needs `one` / `few` / `many` / `other` ("1 úkol", "2 úkoly",
 * "1,5 úkolu", "5 úkolů"); English and German need two. `Intl.PluralRules`
 * decides which, so no catalogue has to encode the rules.
 */
export type Plural = { other: string } & Partial<Record<Intl.LDMLPluralRule, string>>;
export type Message = string | Plural;

/**
 * Every key in the English catalogue.
 *
 * `en` is `as const`, which pins the *keys* as literals — that is what makes a
 * typo in `t('nav.todya')` a compile error.
 */
export type MessageKey = keyof typeof en;

/**
 * A catalogue, in a language whose plural categories are `Categories`.
 *
 * Two things fall out of this that would otherwise be left to discipline. A key
 * that English writes as plural forms must be plural forms in every language —
 * you cannot quietly answer a plural with one string. And Czech must supply
 * `few` and `many`: without `many`, "1,5 úkolu" renders as "1,5 úkolů", which
 * is the kind of wrong nobody notices until a native speaker does.
 */
export type Catalog<Categories extends Intl.LDMLPluralRule = 'one'> = {
  [K in MessageKey]: (typeof en)[K] extends string
    ? string
    : Record<Categories | 'other', string>;
};

/** Values substituted into `{placeholders}`. `count` also selects the plural. */
export type Params = Record<string, string | number>;

const catalogs: Partial<Record<Language, Catalog>> = { en };
const loaders: Record<Language, () => Promise<{ default: Catalog }>> = {
  en: async () => ({ default: en }),
  cs: () => import('./messages/cs').then((module) => ({ default: module.cs })),
  de: () => import('./messages/de').then((module) => ({ default: module.de })),
};

/**
 * Load a language's messages.
 *
 * The other two catalogues are split out of the main bundle: most people use
 * one language and should not download three.
 */
export async function loadCatalog(language: Language): Promise<void> {
  if (catalogs[language]) return;
  catalogs[language] = (await loaders[language]()).default;
}

export function hasCatalog(language: Language): boolean {
  return catalogs[language] !== undefined;
}

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = params[name];
    if (value === undefined) return whole;
    return typeof value === 'number'
      ? new Intl.NumberFormat(getIntlLocale()).format(value)
      : value;
  });
}

function resolve(message: Message, params?: Params): string {
  if (typeof message === 'string') return interpolate(message, params);

  const count = params?.count;
  if (typeof count !== 'number') return interpolate(message.other, params);

  const category = new Intl.PluralRules(getIntlLocale()).select(count);
  return interpolate(message[category] ?? message.other, params);
}

/**
 * Translate one key.
 *
 * Falls back to English rather than showing the key: a partly-translated
 * interface is usable, a screen full of `task.detail.title` is not.
 */
export function translate(key: MessageKey, params?: Params): string {
  const language = getLanguage();
  const message = catalogs[language]?.[key] ?? en[key];
  if (message === undefined) {
    if (import.meta.env.DEV) console.warn(`[i18n] missing message: ${String(key)}`);
    return String(key);
  }
  return resolve(message, params);
}
